import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Divider, Headline, Layout, SearchField, Text, } from '@oneaudi/unified-web-alpha-components';
import { CarCard } from '../car-card/car-card';
import { useCarlineStore } from '../../store/carline';
import { useI18n } from '../../i18n/i18n';
import { TagSelection } from '../tag-selection/tag-selection';
import { StandardEquipmentSections } from './standard-equipment-sections';
import { useDebugMode } from '../../utils/debug-mode';
import { ShowDifferencesToggle } from '../showDifferencesToggle';
import { useTracking } from '../../hooks/use-tracking';
import { EventAction } from '../../types/tracking';
import { SearchWrapper } from './styles/standard-equipment-style';
const groupStandardEquipment = (configuredCarStaticCatalog) => {
    if (!configuredCarStaticCatalog) {
        return [];
    }
    const standardEquipment = configuredCarStaticCatalog.filter((item) => (item === null || item === void 0 ? void 0 : item.type) === 'STANDARD');
    return standardEquipment
        .filter((item) => { var _a; return !((_a = item === null || item === void 0 ? void 0 : item.pr7) === null || _a === void 0 ? void 0 : _a.includes('-in-')); })
        .filter((item) => { var _a; return ((_a = item === null || item === void 0 ? void 0 : item.group) === null || _a === void 0 ? void 0 : _a.id) !== 'Weitere'; })
        .reduce((accumulator, currentItem) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (!currentItem)
            return accumulator;
        const { group, family } = currentItem;
        const groupId = group === null || group === void 0 ? void 0 : group.id;
        const familyId = ((_a = family === null || family === void 0 ? void 0 : family.id) === null || _a === void 0 ? void 0 : _a.includes('.')) ? (_b = family === null || family === void 0 ? void 0 : family.id) === null || _b === void 0 ? void 0 : _b.split('.')[1] : family === null || family === void 0 ? void 0 : family.id;
        const parentPackage = (_d = (_c = standardEquipment
            .find((p) => {
            var _a;
            return (_a = p === null || p === void 0 ? void 0 : p.pr7) === null || _a === void 0 ? void 0 : _a.includes(`${currentItem === null || currentItem === void 0 ? void 0 : currentItem.pr7}-in-`);
        })) === null || _c === void 0 ? void 0 : _c.pr7) === null || _d === void 0 ? void 0 : _d.split('-').pop();
        const parentPackageName = (_e = standardEquipment.find((p) => {
            return (p === null || p === void 0 ? void 0 : p.pr7) === parentPackage;
        })) === null || _e === void 0 ? void 0 : _e.name;
        const finalCurrentItem = Object.assign(Object.assign({}, currentItem), { name: parentPackageName ? `${currentItem.name} - ${parentPackageName}` : currentItem.name });
        const groupIndex = accumulator.findIndex((g) => g.group.id === groupId);
        if (groupIndex === -1) {
            accumulator.push({
                group: {
                    id: groupId,
                    name: group === null || group === void 0 ? void 0 : group.name,
                },
                families: [],
            });
            const newGroupIndex = accumulator.length - 1;
            accumulator[newGroupIndex].families.push({
                id: familyId,
                name: family === null || family === void 0 ? void 0 : family.name,
                items: [finalCurrentItem],
            });
        }
        else {
            const familyIndex = accumulator[groupIndex].families.findIndex((f) => f.id === familyId);
            if (familyIndex === -1) {
                accumulator[groupIndex].families.push({
                    id: familyId,
                    name: family === null || family === void 0 ? void 0 : family.name,
                    items: [finalCurrentItem],
                });
            }
            else {
                (_h = (_g = (_f = accumulator[groupIndex]) === null || _f === void 0 ? void 0 : _f.families[familyIndex]) === null || _g === void 0 ? void 0 : _g.items) === null || _h === void 0 ? void 0 : _h.push(finalCurrentItem);
            }
        }
        return accumulator;
    }, []);
};
const filterGroupedStandardEquipment = (groupedStandardEquipment, selectedTags) => {
    if (!groupedStandardEquipment)
        return [];
    return selectedTags.length === 0
        ? groupedStandardEquipment
        : groupedStandardEquipment.filter((group) => selectedTags.includes(group.group.id));
};
const filterDifferences = (sourceEquipment, compareEquipment) => {
    return sourceEquipment
        .map((group) => {
        const matchingCompareGroup = compareEquipment.find((compareGroup) => compareGroup.group.id === group.group.id);
        if (!matchingCompareGroup)
            return group;
        const filteredFamilies = group.families
            .map((family) => {
            var _a;
            const matchingCompareFamily = matchingCompareGroup.families.find((compareFamily) => compareFamily.id === family.id);
            if (!matchingCompareFamily)
                return family;
            const itemsWithDifferences = (_a = family === null || family === void 0 ? void 0 : family.items) === null || _a === void 0 ? void 0 : _a.filter((item) => {
                var _a;
                const matchingCompareItem = (_a = matchingCompareFamily === null || matchingCompareFamily === void 0 ? void 0 : matchingCompareFamily.items) === null || _a === void 0 ? void 0 : _a.find((compareItem) => (compareItem === null || compareItem === void 0 ? void 0 : compareItem.pr7) === (item === null || item === void 0 ? void 0 : item.pr7));
                return !matchingCompareItem;
            });
            return Object.assign(Object.assign({}, family), { items: itemsWithDifferences });
        })
            .filter((family) => { var _a; return ((_a = family === null || family === void 0 ? void 0 : family.items) === null || _a === void 0 ? void 0 : _a.length) && family.items.length > 0; });
        return Object.assign(Object.assign({}, group), { families: filteredFamilies });
    })
        .filter((group) => group.families.length > 0);
};
const filterBySearch = (groupedStandardEquipment, searchTerm) => {
    if (!searchTerm.trim())
        return groupedStandardEquipment;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return groupedStandardEquipment
        .map((group) => {
        const filteredFamilies = group.families
            .map((family) => {
            var _a;
            const filteredItems = ((_a = family.items) === null || _a === void 0 ? void 0 : _a.filter((item) => {
                var _a;
                return (_a = item === null || item === void 0 ? void 0 : item.name) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(lowerSearchTerm);
            })) || [];
            const familyNameMatches = family.name.toLowerCase().includes(lowerSearchTerm);
            if (familyNameMatches) {
                return family;
            }
            return filteredItems.length > 0
                ? Object.assign(Object.assign({}, family), { items: filteredItems }) : null;
        })
            .filter(Boolean);
        const groupNameMatches = group.group.name.toLowerCase().includes(lowerSearchTerm);
        if (groupNameMatches) {
            return group;
        }
        return filteredFamilies.length > 0
            ? Object.assign(Object.assign({}, group), { families: filteredFamilies }) : null;
    })
        .filter(Boolean);
};
export const EquipmentWithTranslationKeys = [
    'WHEELS',
    'LIGHTS',
    'MATERIALS',
    'STEERING_WHEELS',
    'SEATS',
];
export const StandardEquipment = ({ actions }) => {
    var _a, _b;
    const { i18n } = useI18n();
    const { isDebug, getDebugCode } = useDebugMode();
    const { handleTrackEvent, baseTrackingEventName } = useTracking();
    const [selectedTags, setSelectedTags] = useState([]);
    const [showDifferences, setShowDifferences] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const { appConfig, configuredCar, compare, standardEquipmentInitialCar, standardEquipmentCompareCar, setSelectedEngine, setSecondSelectedEngine, selectedEngine, secondSelectedEngine, } = useCarlineStore();
    const handleChangeInitialCar = (model) => {
        var _a, _b;
        setSelectedEngine(model);
        (_a = actions === null || actions === void 0 ? void 0 : actions.onChangeInitialStandardEquipmentCar) === null || _a === void 0 ? void 0 : _a.call(actions, model);
        (_b = actions === null || actions === void 0 ? void 0 : actions.onChangeInitialTechDataCar) === null || _b === void 0 ? void 0 : _b.call(actions, model);
    };
    const handleChangeCompareCar = (model) => {
        var _a, _b;
        setSecondSelectedEngine(model);
        (_a = actions === null || actions === void 0 ? void 0 : actions.onChangeCompareStandardEquipmentCar) === null || _a === void 0 ? void 0 : _a.call(actions, model);
        (_b = actions === null || actions === void 0 ? void 0 : actions.onChangeCompareTechDataCar) === null || _b === void 0 ? void 0 : _b.call(actions, model);
    };
    const firstEngineStandardEquipment = useMemo(() => {
        if (!standardEquipmentInitialCar)
            return [];
        return groupStandardEquipment(standardEquipmentInitialCar);
    }, [standardEquipmentInitialCar]);
    const secondEngineStandardEquipment = useMemo(() => {
        if (!standardEquipmentCompareCar)
            return [];
        return groupStandardEquipment(standardEquipmentCompareCar);
    }, [standardEquipmentCompareCar]);
    const handleSelectTag = (tag) => {
        const tagExists = selectedTags.includes(tag);
        if (tagExists) {
            setSelectedTags((prev) => prev.filter((t) => t !== tag));
        }
        else {
            setSelectedTags((prev) => [...prev, tag]);
        }
    };
    const handleResetFilters = () => {
        setSelectedTags([]);
    };
    const handleSearchFocusTrackEvent = () => {
        const eventName = `${baseTrackingEventName} - focus on search field`;
        handleTrackEvent({
            eventInfo: {
                eventName,
                eventAction: EventAction.Content,
            },
            attributes: {
                elementName: 'text field',
            },
        });
    };
    const availableTags = useMemo(() => {
        var _a;
        let filteredGroups = firstEngineStandardEquipment;
        filteredGroups = filterBySearch(filteredGroups, searchValue);
        if (showDifferences && compare) {
            filteredGroups = filterDifferences(filteredGroups, secondEngineStandardEquipment);
        }
        return ((_a = filteredGroups === null || filteredGroups === void 0 ? void 0 : filteredGroups.map((group) => {
            const tagName = group.group.name;
            const finalTagName = EquipmentWithTranslationKeys.includes(tagName)
                ? i18n(`compareLayer.standardEquipment.sections.${tagName}`)
                : tagName;
            const totalItemsWithFamilyItems = group.families.reduce((acc, family) => { var _a, _b; return acc + ((_b = (_a = family === null || family === void 0 ? void 0 : family.items) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0); }, 0);
            return {
                key: group.group.id,
                value: group.group.id,
                label: `${finalTagName} (${totalItemsWithFamilyItems})`,
            };
        })) !== null && _a !== void 0 ? _a : []);
    }, [
        firstEngineStandardEquipment,
        searchValue,
        showDifferences,
        compare,
        secondEngineStandardEquipment,
        i18n,
    ]);
    const filteredGroupedStandardEquipment = useMemo(() => {
        let filtered = filterGroupedStandardEquipment(firstEngineStandardEquipment, selectedTags);
        filtered = filterBySearch(filtered, searchValue);
        if (showDifferences && compare) {
            filtered = filterDifferences(filtered, secondEngineStandardEquipment);
        }
        return filtered;
    }, [
        firstEngineStandardEquipment,
        selectedTags,
        searchValue,
        showDifferences,
        compare,
        secondEngineStandardEquipment,
    ]);
    const filteredSecondGroupedStandardEquipment = useMemo(() => {
        let filtered = filterGroupedStandardEquipment(secondEngineStandardEquipment, selectedTags);
        filtered = filterBySearch(filtered, searchValue);
        if (showDifferences && compare) {
            filtered = filterDifferences(filtered, firstEngineStandardEquipment);
        }
        return filtered;
    }, [
        secondEngineStandardEquipment,
        selectedTags,
        searchValue,
        showDifferences,
        compare,
        firstEngineStandardEquipment,
    ]);
    const nameToShow = (_b = (_a = configuredCar.carlineStructureCarline) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : 'Carline';
    return (_jsxs(Layout, { margin: ['40px', 0, 0, 0], direction: "column", rowGap: "40px", children: [compare && (_jsxs(Headline, { tag: "h1", variant: {
                    order: '2',
                    type: 'headline',
                    weight: 'normal',
                }, children: [nameToShow, isDebug && (_jsxs(_Fragment, { children: [_jsx("br", {}), _jsx(Text, { tag: "span", variant: { type: 'copy', order: '2', style: 'normal' }, children: ` [${selectedEngine ? getDebugCode(selectedEngine) : ''}]` })] }))] })), _jsxs("div", { style: { display: 'flex', gap: '18px' }, children: [_jsx(CarCard, { isSelectedDriveTrain: appConfig === null || appConfig === void 0 ? void 0 : appConfig.isSelectedDriveTrain, setSelectedEngine: (model) => {
                            handleChangeInitialCar(model);
                        }, selectedEngine: selectedEngine }), compare && (_jsx(CarCard, { isSelectedDriveTrain: appConfig === null || appConfig === void 0 ? void 0 : appConfig.isSelectedDriveTrainCompare, setSelectedEngine: (model) => {
                            handleChangeCompareCar(model);
                        }, selectedEngine: secondSelectedEngine, hasNoDefaultEngine: true }))] }), _jsx(Divider, {}), _jsx(Layout, { direction: "column", rowGap: "25px", children: _jsx(TagSelection, { title: i18n('compareLayer.header.tagSelection.title'), subTitle: i18n('compareLayer.header.tagSelection.subTitle'), tags: availableTags, selectedTags: selectedTags, onSelect: handleSelectTag, onResetFilters: handleResetFilters }) }), _jsx(Divider, {}), _jsxs(Layout, { direction: {
                    xs: 'column',
                    md: 'row',
                }, justify: {
                    xs: 'flex-start',
                    md: 'space-between',
                }, align: {
                    xs: 'flex-start',
                    md: 'center',
                }, children: [_jsx(SearchWrapper, { "$compare": compare, children: _jsx(SearchField, { clearButton: {
                                'aria-label': i18n('compareLayer.standardEquipment.search.ariaLabel'),
                                onClick: () => {
                                    setSearchValue('');
                                },
                            }, onClick: handleSearchFocusTrackEvent, value: searchValue, id: "standard-equipment-search-field", label: i18n('compareLayer.standardEquipment.search.label'), onChange: (e) => {
                                setSearchValue(e.target.value);
                            } }) }), compare && (_jsx(ShowDifferencesToggle, { showDifferences: showDifferences, setShowDifferences: setShowDifferences }))] }), firstEngineStandardEquipment && (_jsx(StandardEquipmentSections, { groupedStandardEquipment: filteredGroupedStandardEquipment, secondGroupedStandardEquipment: filteredSecondGroupedStandardEquipment }))] }));
};
